import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
} from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { generateRankingCertificatePDF, generateTrainingCertificatePDF, generateFoundationCertificatePDF } from '@utils/generateCertificatesPDF';
import {
  FOUNDATION_CERT_LEVELS,
  foundationCertificateNo,
  listFoundationCertificationsForUser,
} from '@utils/contestCertification';
import { getGradeColor } from '@config/brandTokens';
import { RELEASE_SPORTS } from '@config/releaseProfile';
import { loadMergedSportContestCatalog } from '@utils/contestCatalogApi';
import { getContestsGroupedByCategory } from '@utils/contestCatalog';
import { uniqueCatalogContests } from '@utils/contestSelfEvaluation';
import type { ContestCatalogEntry } from '../../types/contestCatalog';
import { useAuthStore } from '@store/authStore';

const SPORTS = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];

function periodLabelFromTraining(report: any): string {
  const period = String(report.period || '');
  const start = report.dateRange?.start ? new Date(report.dateRange.start) : null;
  const year = start ? start.getFullYear() : new Date().getFullYear();

  if (period.startsWith('month-')) {
    const monthIndex = parseInt(period.split('-')[1], 10) - 1;
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return `${months[monthIndex] || period} ${year}`;
  }
  if (period.startsWith('quarter-')) {
    const q = period.split('-')[1];
    return `Q${q} ${year}`;
  }
  if (period === 'yearly') return `Yearly ${year}`;
  return period || String(year);
}

function assessmentLabel(report: any): string {
  const t = report.assessmentType === 'quick' || report.assessmentType === 'high-level'
    ? 'Skill Assessment High-level'
    : report.assessmentType === 'advanced' || report.assessmentType === 'low-level'
      ? 'Skill Assessment Low-level'
      : 'Skill Assessment';
  return t;
}

function periodLabelFromPeriodKey(periodKey: string): string {
  if (!periodKey) return 'N/A';
  const [p, y] = String(periodKey).split('-');
  if (!p || !y) return periodKey;
  if (p === 'Y') return `Yearly ${y}`;
  if (p === 'H1') return `H1 ${y}`;
  if (p === 'H2') return `H2 ${y}`;
  if (p.startsWith('Q')) return `${p} ${y}`;
  return periodKey;
}

export default function PlayerCertificates({ player }: { player: any }) {
  const { user } = useAuthStore();
  const userId = String(user?.id || player?.userId || player?.user?.id || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkedCoachNameById, setLinkedCoachNameById] = useState<Record<string, string>>({});
  const [trainingCoachEvals, setTrainingCoachEvals] = useState<any[]>([]);
  const [practicalRankingReports, setPracticalRankingReports] = useState<any[]>([]);
  const [contestsBySport, setContestsBySport] = useState<Record<string, ContestCatalogEntry[]>>({});

  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      RELEASE_SPORTS.map(async (sport) => {
        const { catalog } = await loadMergedSportContestCatalog(sport, 'foundation');
        if (!catalog) return [sport, []] as const;
        const contests = uniqueCatalogContests(
          getContestsGroupedByCategory(catalog, 'foundation').flatMap((group) => group.contests),
        );
        return [sport, contests] as const;
      }),
    ).then((rows) => {
      if (cancelled) return;
      setContestsBySport(Object.fromEntries(rows));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const foundationCerts = useMemo(
    () => (userId ? listFoundationCertificationsForUser(userId, RELEASE_SPORTS, undefined, contestsBySport) : []),
    [userId, player?.id, contestsBySport],
  );

  useEffect(() => {
    const loadLinkedCoaches = async () => {
      if (!player?.id) return;
      try {
        const resp = await fetch(`/api/players/${player.id}/coaches`);
        const data = await resp.json();
        if (data?.success && Array.isArray(data.data)) {
          const map: Record<string, string> = {};
          data.data.forEach((c: any) => {
            if (!c?.id) return;
            const id = String(c.id);
            const name =
              (c.name && String(c.name).trim()) ||
              `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
              c.coachIdCode ||
              `Coach ${id.substring(0, 8)}`;
            map[id] = name;
          });
          setLinkedCoachNameById(map);
        }
      } catch (e) {
        console.error('Error loading linked coaches for certificates:', e);
      }
    };
    loadLinkedCoaches();
  }, [player?.id]);

  useEffect(() => {
    const loadCertificates = async () => {
      if (!player?.id) return;
      setLoading(true);
      setError(null);
      try {
        // Training: include coach-evaluated submitted evaluations across sports
        const allTraining: any[] = [];
        for (const sport of SPORTS) {
          try {
            const r = await fetch(`/api/performance/basic/player/${player.id}?sport=${encodeURIComponent(sport)}`);
            const j = await r.json();
            if (j?.success && Array.isArray(j.data)) {
              j.data
                .filter((x: any) => (x.submitted === true || x.submitted === 'true' || x.submitted === 1))
                .filter((x: any) => !!x.coachId) // coach evaluation only
                .forEach((x: any) => allTraining.push({ ...x, sport }));
            }
          } catch (e) {
            // Non-fatal: some sports may not have tables yet
          }
        }
        setTrainingCoachEvals(allTraining);

        // Ranking: practical test certificates (v2)
        try {
          const r = await fetch(`/api/ranking-reports/player/${player.id}?categoryId=practical-test`);
          const j = await r.json();
          if (j?.success && Array.isArray(j.data)) {
            setPracticalRankingReports(j.data);
          } else {
            setPracticalRankingReports([]);
          }
        } catch (e) {
          setPracticalRankingReports([]);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load certificates');
      } finally {
        setLoading(false);
      }
    };
    loadCertificates();
  }, [player?.id]);

  const sortedTraining = useMemo(() => {
    return [...trainingCoachEvals].sort((a, b) => String(b.submittedAt || '').localeCompare(String(a.submittedAt || '')));
  }, [trainingCoachEvals]);

  const sortedPractical = useMemo(() => {
    return [...practicalRankingReports].sort((a, b) => String(b.submittedAt || '').localeCompare(String(a.submittedAt || '')));
  }, [practicalRankingReports]);

  if (!player?.id) {
    return <Alert severity="info">Player not loaded.</Alert>;
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          color: '#fff',
          background: 'radial-gradient(circle at 14% 22%, rgba(255,255,255,0.24), transparent 24%), linear-gradient(135deg, #14532d 0%, #16a34a 48%, #0f766e 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 170, height: 170, right: -48, top: -62, borderRadius: '50%', border: '28px solid rgba(255,255,255,0.14)' }} />
        <Typography variant="h4" sx={{ fontWeight: 950, position: 'relative' }}>
          Certificates
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.86)', mt: 0.5, position: 'relative' }}>
          Download Technical Foundation, coach evaluation, and ranking certificates from one place.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #bfdbfe', background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 58%, #dbeafe 100%)', boxShadow: '0 14px 32px rgba(37, 99, 235, 0.10)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#1e3a8a' }}>
                  Technical Foundation Certificates
                </Typography>
                <Chip label="Contest grades D–A+" size="small" sx={{ bgcolor: '#fff', color: '#1e3a8a', border: '1px solid #93c5fd', fontWeight: 900 }} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Same grades as contest benchmarks and learning: D, C, B, A, A+. The certificate level is the lowest skill grade for that sport and age. Available for {RELEASE_SPORTS.join(', ')}.
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                {FOUNDATION_CERT_LEVELS.map((lvl) => {
                  const colors = getGradeColor(lvl.code);
                  return (
                    <Chip
                      key={lvl.code}
                      size="small"
                      label={lvl.code}
                      sx={{ fontWeight: 800, bgcolor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
                    />
                  );
                })}
              </Box>
              {foundationCerts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No Foundation certificates yet. Complete Self Evaluation on a contest sport page, then return here to download.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: '#bfdbfe' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#dbeafe' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Sport</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Age</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Score</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Skills</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {foundationCerts.map((cert) => {
                        const colors = getGradeColor(cert.levelCode);
                        return (
                          <TableRow key={`${cert.sport}-${cert.ageCategory}`} hover>
                            <TableCell>
                              <Chip size="small" label={cert.sport} sx={{ fontWeight: 800 }} />
                            </TableCell>
                            <TableCell>{cert.ageCategory}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={cert.levelCode}
                                sx={{ fontWeight: 900, bgcolor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
                              />
                            </TableCell>
                            <TableCell>{cert.scorePercent.toFixed(1)}</TableCell>
                            <TableCell>
                              {cert.gradedCount}/{cert.skillCount || cert.gradedCount}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<PdfIcon />}
                                onClick={() => {
                                  const certNo = foundationCertificateNo(cert, player?.playerIdCode || player?.id);
                                  generateFoundationCertificatePDF({
                                    player,
                                    sport: cert.sport,
                                    ageCategory: cert.ageCategory,
                                    levelCode: cert.levelCode,
                                    levelLabel: cert.levelLabel,
                                    scorePercent: cert.scorePercent,
                                    skillsAssessed: cert.gradedCount,
                                    certificateNo: certNo,
                                    issuedOn: cert.updatedAt
                                      ? new Date(cert.updatedAt).toLocaleDateString()
                                      : undefined,
                                  });
                                }}
                              >
                                Download
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #bbf7d0', background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 58%, #dcfce7 100%)', boxShadow: '0 14px 32px rgba(22, 163, 74, 0.12)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#166534' }}>
                  Training Certificates
                </Typography>
                <Chip label="Coach Evaluation" size="small" sx={{ bgcolor: '#fff', color: '#166534', border: '1px solid #86efac', fontWeight: 900 }} />
              </Box>
              {sortedTraining.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No submitted coach-evaluated training reports found.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: '#bbf7d0' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#dcfce7' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Sport</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Assessment</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Evaluated By</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedTraining.map((r) => {
                        const evaluatedBy = linkedCoachNameById[String(r.coachId)] || 'Coach';
                        return (
                          <TableRow key={String(r.id)} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#f7fef9' } }}>
                            <TableCell><Chip size="small" label={r.sport || 'N/A'} sx={{ bgcolor: '#ecfdf5', color: '#166534', fontWeight: 800 }} /></TableCell>
                            <TableCell>{assessmentLabel(r)}</TableCell>
                            <TableCell>{periodLabelFromTraining(r)}</TableCell>
                            <TableCell>{evaluatedBy}</TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<PdfIcon />}
                                onClick={() => {
                                  const certNo = `TRN-${String(r.id).slice(0, 8)}`;
                                  generateTrainingCertificatePDF({
                                    player,
                                    sport: r.sport || 'Badminton',
                                    periodLabel: periodLabelFromTraining(r),
                                    assessmentLabel: assessmentLabel(r),
                                    averageRating: Number(r.averageRating || 0),
                                    evaluatedBy,
                                    certificateNo: certNo,
                                    issuedOn: r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : undefined,
                                  });
                                }}
                              >
                                Download
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, border: '1px solid #fed7aa', background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 58%, #ffedd5 100%)', boxShadow: '0 14px 32px rgba(217, 119, 6, 0.12)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#c2410c' }}>
                  Ranking Certificates
                </Typography>
                <Chip label="EncycGames Board Exam" size="small" sx={{ bgcolor: '#fff', color: '#c2410c', border: '1px solid #fdba74', fontWeight: 900 }} />
              </Box>
              {sortedPractical.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No submitted practical test ranking reports found.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: '#fed7aa' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#ffedd5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Sport</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Skills Assessed</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedPractical.map((r: any) => (
                        <TableRow key={String(r.reportId)} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fffaf5' } }}>
                          <TableCell><Chip size="small" label={r.sport || 'Badminton'} sx={{ bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 800 }} /></TableCell>
                          <TableCell>{periodLabelFromPeriodKey(String(r.periodKey || ''))}</TableCell>
                          <TableCell>{String(r.grade || 'C')}</TableCell>
                          <TableCell>{Number(r.totalStrokes || 0)}</TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PdfIcon />}
                              onClick={() => {
                                const certNo = `RANK-${String(r.reportId).slice(0, 8)}`;
                                generateRankingCertificatePDF({
                                  player,
                                  sport: String(r.sport || 'Badminton'),
                                  periodLabel: periodLabelFromPeriodKey(String(r.periodKey || '')),
                                  categoryLabel: 'Board Exam',
                                  score: Number(r.score || 0),
                                  maxScore: Number(r.maxScore || 0),
                                  percentage: String(r.percentage || '0.0'),
                                  grade: String(r.grade || 'C'),
                                  skillsAssessed: Number(r.totalStrokes || 0),
                                  certificateNo: certNo,
                                  issuedOn: r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : undefined,
                                });
                              }}
                            >
                              Download
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}








